"use client";

import type React from "react";
import { useState, useRef } from "react";
import {
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Tooltip,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Text,
} from "@chakra-ui/react";
import { Settings, ChevronDown } from "lucide-react";

interface BackgroundSoundSelectProps {
  value: string | null | undefined;
  onChange: (value: string | null) => Promise<boolean> | void;
}

const backgroundSoundOptions = [
  { value: null, label: "None" },
  { value: "coffee-shop", label: "Coffee Shop" },
  { value: "convention-hall", label: "Convention Hall" },
  { value: "summer-outdoor", label: "Summer Outdoor" },
  { value: "mountain-outdoor", label: "Mountain Outdoor" },
  { value: "static-noise", label: "Static Noise" },
  { value: "call-center", label: "Call Center" },
];

export const BackgroundSoundSelect: React.FC<BackgroundSoundSelectProps> = ({
  value,
  onChange,
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const selectedOption =
    backgroundSoundOptions.find((o) => o.value === value) ||
    backgroundSoundOptions[0];

  const handleChange = async (newValue: string | null) => {
    setIsUpdating(true);
    try {
      await onChange(newValue);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <FormControl>
      <HStack justify="space-between" align="flex-start" mb={2}>
        <FormLabel fontSize="sm" fontWeight="medium" color="gray.700" mb={0}>
          Background Sound
        </FormLabel>
        <Tooltip label="Advanced settings" placement="top">
          <IconButton
            aria-label="Advanced settings"
            icon={<Settings size={16} />}
            size="xs"
            variant="ghost"
            visibility="hidden"
          />
        </Tooltip>
      </HStack>

      <Menu>
        <MenuButton
          as={Button}
          ref={buttonRef}
          rightIcon={<ChevronDown size={16} />}
          size="sm"
          variant="outline"
          colorScheme="primary"
          color="primary.600"
          borderColor="primary.300"
          bg="white"
          _hover={{ bg: "primary.50", borderColor: "primary.400" }}
          _active={{ bg: "primary.100", borderColor: "primary.500" }}
          _focusVisible={{
            borderColor: "primary.500",
            boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
          }}
          isDisabled={isUpdating}
          width="100%"
          textAlign="left"
          fontWeight="normal"
        >
          <Text as="span" fontSize="sm">
            {selectedOption.label}
          </Text>
        </MenuButton>
        <MenuList
          maxH="220px"
          overflowY="auto"
          borderColor="primary.200"
          minW={
            buttonRef.current ? `${buttonRef.current.offsetWidth}px` : "100%"
          }
          _focusWithin={{
            borderColor: "primary.300",
            boxShadow: "0 0 0 1px var(--chakra-colors-primary-300)",
          }}
          sx={{
            scrollbarWidth: "thin",
            scrollbarColor: "var(--chakra-colors-primary-400) transparent",
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
              borderRadius: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "var(--chakra-colors-primary-400)",
              borderRadius: "8px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "var(--chakra-colors-primary-500)",
            },
          }}
        >
          {backgroundSoundOptions.map((option) => (
            <MenuItem
              key={option.value === null ? "null" : option.value}
              onClick={() => handleChange(option.value)}
              _hover={{ bg: "primary.50" }}
              _focus={{ bg: "primary.50" }}
              _active={{ bg: "primary.100" }}
              fontSize="sm"
            >
              {option.label}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </FormControl>
  );
};
